import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { PlanetSyncProvider } from '@/components/PlanetSyncProvider';
import WorldEngine from '@/components/WorldEngine';
import { GlobalAudioPlayerProvider } from '@/components/GlobalAudioPlayer';
import SiteSettingsApplier from '@/components/SiteSettingsApplier';
import ExperienceApplier from '@/components/ExperienceApplier';
import PGTracker from '@/components/PGTracker';
import SuggestionRuntime from '@/components/SuggestionRuntime';
import HomePage from '@/pages/HomePage';
import WatchPage from '@/pages/WatchPage';
import MusicPage from '@/pages/MusicPage';
import PodcastsPage from '@/pages/PodcastsPage';
import { ReadList, ArticleDetail } from '@/pages/ReadPage';
import MeetGPage from '@/pages/MeetGPage';
import CollabPage from '@/pages/CollabPage';
import { CollectList, ProductDetail } from '@/pages/CollectPage';
import CheckoutSuccessPage from '@/pages/CheckoutSuccessPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AdminDashboardPage from '@/pages/AdminDashboardPage';
import LegalPage from '@/pages/LegalPage';
import CheckoutPage from '@/pages/CheckoutPage';
import DynamicChannelPage from '@/pages/DynamicChannelPage';
import PreviewHomePage from '@/pages/PreviewHomePage';
import { Toaster } from 'sonner';

function NotFound() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="pg-card p-10 text-center max-w-md">
        <p className="font-mono text-[11px] tracking-[0.32em] pg-text-glow-pink">404 — OFF AIR</p>
        <h1 className="font-display text-3xl mt-3 tracking-[0.04em]">THIS CHANNEL DOESN'T EXIST.</h1>
        <p className="text-[var(--pg-ink-muted)] mt-3">Tune back to the main signal.</p>
        <Link to="/" className="mt-6 inline-block pg-btn-neon">Return home</Link>
      </div>
    </div>
  );
}

function LegacyRedirect({ to }) {
  const parts = window.location.pathname.split('/').filter(Boolean);
  const tail = parts.slice(1).join('/');
  return <Navigate to={tail ? `${to}/${tail}` : to} replace />;
}

function App() {
  return (
    <div className="App">
      <PlanetSyncProvider>
        <SiteSettingsApplier />
        <ExperienceApplier />
        <WorldEngine />
        <GlobalAudioPlayerProvider>
          <BrowserRouter>
            <PGTracker />
            <SuggestionRuntime />
            <Routes>
            <Route path="/" element={<Layout><HomePage /></Layout>} />

            {/* The destinations */}
            <Route path="/watch" element={<Layout><WatchPage /></Layout>} />
            <Route path="/music" element={<Layout><MusicPage /></Layout>} />
            <Route path="/podcasts" element={<Layout><PodcastsPage /></Layout>} />
            <Route path="/on-my-mind" element={<Layout><ReadList /></Layout>} />
            <Route path="/on-my-mind/:slug" element={<Layout><ArticleDetail /></Layout>} />
            <Route path="/meet-g" element={<Layout><MeetGPage /></Layout>} />
            <Route path="/collab" element={<Layout><CollabPage /></Layout>} />
            <Route path="/legal/:slug" element={<Layout><LegalPage /></Layout>} />
            <Route path="/checkout/:id" element={<Layout><CheckoutPage /></Layout>} />

            {/* Vault (paid products) — direct routes preserved for Stripe success */}
            <Route path="/vault" element={<Layout><CollectList /></Layout>} />
            <Route path="/vault/success" element={<Layout><CheckoutSuccessPage /></Layout>} />
            <Route path="/vault/:id" element={<Layout><ProductDetail /></Layout>} />
            <Route path="/collect/success" element={<Layout><CheckoutSuccessPage /></Layout>} />
            <Route path="/collect/:id" element={<Layout><ProductDetail /></Layout>} />

            {/* Legacy redirects */}
            <Route path="/listen" element={<Navigate to="/music" replace />} />
            <Route path="/read" element={<Navigate to="/on-my-mind" replace />} />
            <Route path="/read/:slug" element={<LegacyRedirect to="/on-my-mind" />} />
            <Route path="/collect" element={<Navigate to="/vault" replace />} />
            <Route path="/cinema" element={<Navigate to="/watch" replace />} />
            <Route path="/record-room" element={<Navigate to="/music" replace />} />
            <Route path="/observation-room" element={<Navigate to="/on-my-mind" replace />} />
            <Route path="/observation-room/:slug" element={<LegacyRedirect to="/on-my-mind" />} />
            <Route path="/gallery" element={<Navigate to="/watch" replace />} />
            <Route path="/greenhouse" element={<Navigate to="/" replace />} />

            <Route path="/admin/login" element={<Layout><AdminLoginPage /></Layout>} />
            <Route path="/admin" element={<Layout><AdminDashboardPage /></Layout>} />
            {/* Full-page draft preview for the Homepage Director */}
            <Route path="/preview/home" element={<Layout><PreviewHomePage /></Layout>} />
            {/* Dynamic channel route — matches any single-segment path not consumed above.
                Renders DynamicChannelPage, which resolves the channel from /api/channels
                and shows a 404 view if the channel isn't in the DB. Must come BEFORE the
                catch-all "*" route. */}
            <Route path="/:channelKey" element={<Layout><DynamicChannelPage /></Layout>} />
            <Route path="*" element={<Layout><NotFound /></Layout>} />
          </Routes>
        </BrowserRouter>
        </GlobalAudioPlayerProvider>
        <Toaster theme="dark" position="bottom-center" />
      </PlanetSyncProvider>
    </div>
  );
}

export default App;
